import PropTypes from 'prop-types'

import React from 'react'
import { pure, compose, branch } from 'recompose'
import { connect } from 'react-redux'
import uuid from 'uuid'

import withReduxStore from 'data/withReduxStore'
import { providesCreateThread } from 'screens/CommentPanel/dataSync'
import { makesMutations } from 'data/graphql'

import { noteQuery, noteByTokenQuery } from 'components/Notes/dataSync/note'
import {
  provideDocStatus,
  provideEditorValue,
  DocStatusTypes,
} from '@slite/editor'
import { selectors as NotesSelectors } from 'data/notes'
import { selectors as UserPreferencesSelectors } from 'data/userPreferences'
import { selectors as OrganizationSelectors } from 'data/organizations'
import {
  actions as ConfigActions,
  selectors as ConfigSelectors,
} from 'data/config'
import { selectors as UserSelectors } from 'data/users'
import { selectors as UISelectors } from 'data/ui'
import { selectors as ApplicationsSelectors } from 'data/applications'
import RoutesHelper, { getAbsolutePath } from 'helpers/RoutesHelper'
import { uploadImage, addMediaFileToNote } from 'data/medias/actions'
import providesUnfurlIntegrationUrl from 'components/Integrations/dataSync/providesUnfurlIntegrationUrl'
import { providesUserPreferencesMutations } from 'components/Settings/User/dataSync'
import { createInfoToasterMessage, setActiveModal } from 'data/ui/actions'
import { track } from 'data/analytics/actions'

import Editor from '../Editor'
import NotePlaceholder from './Placeholder'
import createNotePlugins from '../plugins/createNotePlugins'
import searchNotes from '../utils/searchNotes'
import searchUsers from '../utils/searchUsers'
import makeUnfurlHandler from '../plugins/unfurl/handler'
import makeIframelyEmbedExtractor from '../plugins/unfurl/extractors/makeIframelyExtractor'
import makeNoteLinkExtractor from '../plugins/unfurl/extractors/makeNoteLinkExtractor'
import makeIntegrationTileExtractor from '../plugins/unfurl/extractors/makeIntegrationTileExtractor'
import withComponent from 'components/Utils/hoc/withComponent'
import withRouterActions from 'routes/utils/withRouterActions'

const { CONNECTED } = UISelectors.CONNECTION_STATUSES_TYPE
const { DOC_STATUS_ERROR } = DocStatusTypes

class NoteEditor extends React.Component {
  static propTypes = {
    value: PropTypes.object,
    focusPrev: PropTypes.func.isRequired,
    createThread: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    onEditorRef: PropTypes.func.isRequired,
    createInfoToasterMessage: PropTypes.func.isRequired,
    setActiveModal: PropTypes.func.isRequired,
    track: PropTypes.func,
  }

  static defaultProps = {
    onChange: () => {},
    focusPrev: () => {},
  }

  constructor(props) {
    super(props)

    const iframelyExtractor = makeIframelyEmbedExtractor(
      this.props.iframelyApiKey
    )
    const integrationTileExtractor = makeIntegrationTileExtractor({
      getApplications: this.getApplications,
      onIntegrationTileCreated: this.onIntegrationTileCreated,
      getNewIntegrationTileAttributes: this.getNewIntegrationTileAttributes,
      isEnableAppSuppressed: this.isEnableAppSuppressed,
      isConnectAppSuppressed: this.isConnectAppSuppressed,
    })

    const noteLinkExtractor = makeNoteLinkExtractor({
      showNote: this.props.showNote,
      showNoteByToken: this.props.showNoteByToken,
      appDomain: props.appDomain,
      organizationDomain: props.organization.domain,
    })

    const unfurlHandler = makeUnfurlHandler([
      // order is important here
      noteLinkExtractor,
      integrationTileExtractor,
      iframelyExtractor,
    ])

    this.plugins = createNotePlugins({
      getNoteId: () => this.props.noteId,
      hasEditorNextFeatureFlag: props.hasEditorNextFeatureFlag,
      hasDatabaseFeatureFlag: this.props.hasDatabaseFeatureFlag,
      focusPrevious: this.props.focusPrev,
      getFullNotePath: this.getFullNotePath,
      getUser: this.getUser,
      uploadImage: this.props.uploadImage,
      createThread: this.createThread,
      fetchUsers: this.fetchUsers,
      fetchNotes: this.fetchNotes,
      getCurrentUser: this.getCurrentUser,
      addMediaFileToNote: this.props.addMediaFileToNote,
      iframelyApiKey: this.props.iframelyApiKey,
      unfurlHandler,
      getApplications: this.getApplications,
      integrationSuggestionCallbacks: {
        onClose: this.onIntegrationSuggestionClose,
        onEnableApp: this.onIntegrationSuggestionFollow,
        onConnectApp: this.handleIntegrationSuggestionConnect,
        onSuppress: this.onIntegrationSuggestionSuppress,
      },
      createInfoToasterMessage: this.props.createInfoToasterMessage,
      track: this.props.track,
      appDomain: props.appDomain,
      organizationDomain: props.organization.domain,
      showNoteByToken: this.props.showNoteByToken,
      setActiveModal: this.props.setActiveModal,
    })

    this.state = {
      loadingState: <NotePlaceholder />,
    }
  }

  getFullNotePath = noteId => {
    const note = NotesSelectors.getNote(
      this.props.reduxStore.getState(),
      noteId
    )
    if (!note) {
      return null
    }
    const { channelId } = note
    const relativePath = RoutesHelper.noteChannelShow({ channelId, noteId })
    return getAbsolutePath(relativePath)
  }

  fetchUsers = (query, maxItemsOnEmptyQuery) => {
    return searchUsers(
      this.props.reduxStore.getState(),
      query,
      maxItemsOnEmptyQuery
    )
  }

  fetchNotes = query => {
    return searchNotes(this.props.reduxStore.getState(), query, {
      fetchSearchToken: this.props.fetchSearchToken,
    })
  }

  getCurrentUser = () => {
    return UserSelectors.getCurrentUser(this.props.reduxStore.getState()).toJS()
  }

  getApplications = () => {
    return ApplicationsSelectors.getApplications(
      this.props.reduxStore.getState()
    ).map(application => application.toJS())
  }

  isEnableAppSuppressed = clientId => {
    return (
      this.props.userPreferences.suppressedEnableApp &&
      this.props.userPreferences.suppressedEnableApp.includes(clientId)
    )
  }

  isConnectAppSuppressed = clientId => {
    return (
      this.props.userPreferences.suppressedConnectApp &&
      this.props.userPreferences.suppressedConnectApp.includes(clientId)
    )
  }

  getNewIntegrationTileAttributes = application => {
    return {
      tileId: uuid(),
      createdBy: this.props.activeUserId,
      iconURL: application.photoURL,
    }
  }

  onIntegrationTileCreated = ({ tileId, url }) => {
    const { noteId } = this.props
    this.props.unfurlIntegrationUrl({ noteId, tileId, url })
  }

  getUser = userId => {
    return UserSelectors.getUser(this.props.reduxStore.getState(), userId)
  }

  onIntegrationSuggestionClose = ({ clientId }) => {
    // analytics?
  }

  handleIntegrationSuggestionConnect = ({ clientId }) => {
    // analytics?
  }

  onIntegrationSuggestionFollow = ({ clientId }) => {
    this.props.routerPush(
      RoutesHelper.settingsIntegrationApplicationDetails({
        applicationId: clientId,
        redirectUri: window.location.href,
      })
    )
  }

  onIntegrationSuggestionSuppress = ({ clientId }) => {
    const { suppressedEnableApp = [] } = this.props.userPreferences
    this.props.updateUserPreferences({
      suppressedEnableApp: [...suppressedEnableApp, clientId],
    })
  }

  createThread = ({ threadId = uuid(), content, entities, highlight }) => {
    const thread = {
      noteId: this.props.noteId,
      threadId,
      content,
      entities,
      highlight,
    }
    this.props.createThread({ thread })
    return threadId
  }

  render() {
    const {
      value,
      onChange,
      docStatus,
      isCollabDisconnected,
      userCanEdit,
      onEditorRef,
    } = this.props

    const readOnly =
      !userCanEdit || docStatus === DOC_STATUS_ERROR || isCollabDisconnected

    const EditorComponent = this.props.Component

    return (
      <div
        className="note-editor width-full notranslate position-relative fs-block data-hj-suppress" /* fs-block & data-hj-suppress hides content for FullStory & Hotjar */
        translate="no"
      >
        <EditorComponent
          noteEditor
          placeholder={
            !readOnly ? 'Click here to start writing' : 'This doc is blank'
          }
          loadingState={this.state.loadingState}
          plugins={this.plugins}
          readOnly={readOnly}
          onChange={onChange}
          onEditorRef={onEditorRef}
          value={value}
          hasEditorNextFeatureFlag={this.props.hasEditorNextFeatureFlag}
        />
      </div>
    )
  }
}

export default compose(
  pure,
  providesUnfurlIntegrationUrl,
  makesMutations({
    query: noteQuery,
    mutationPropName: 'showNote',
  }),
  makesMutations({
    query: noteByTokenQuery,
    mutationPropName: 'showNoteByToken',
  }),
  withRouterActions,
  withReduxStore,
  connect(
    state => ({
      hasEditorNextFeatureFlag: ConfigSelectors.hasFlag(state, 'editor-next'),
      hasDatabaseFeatureFlag: ConfigSelectors.hasFlag(state, 'database'),
      iframelyApiKey: ConfigSelectors.getIframelyApiKey(state),
      activeUserId: UISelectors.getActiveUserId(state),
      userPreferences: UserPreferencesSelectors.getUserPreferences(state),
      isCollabDisconnected:
        UISelectors.getConnectionStatus(state, 'collab') !== CONNECTED,
      appDomain: ConfigSelectors.getAppDomain(state),
      organization: OrganizationSelectors.getOrganization(state),
    }),
    {
      uploadImage,
      fetchSearchToken: ConfigActions.fetchSearchToken,
      addMediaFileToNote,
      createInfoToasterMessage,
      track,
      setActiveModal,
    }
  ),
  providesCreateThread,
  providesUserPreferencesMutations,
  provideDocStatus(),
  branch(
    ({ hasEditorNextFeatureFlag }) => hasEditorNextFeatureFlag,
    withComponent(() =>
      import(/* webpackChunkName: "editor-next" */ './NoteEditorNext')
    ),
    withComponent(() => provideEditorValue()(Editor))
  )
)(NoteEditor)
